import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { CreditCard, Copy, ExternalLink, Search, FileText, Download, Edit2, X, RotateCcw, ChevronDown, Check, Calendar as CalendarIcon, MailCheck, MailX, MailWarning } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../utils/api';
import RefundRequestsTab from '../components/payments/RefundRequestsTab';

const API_URL = getApiUrl();

const STATUS_OPTIONS = [
  { value: 'created', label: 'Created' },
  { value: 'sent', label: 'Sent' },
  { value: 'captured', label: 'Captured' },
  { value: 'paid', label: 'Paid' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

// V36.4 — Date range presets. Values are YYYY-MM-DD in user's local (IST) date.
const fmt = (d) => d.toISOString().split('T')[0];
const buildDatePreset = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === 'this_month') {
    return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
  }
  if (key === 'last_month') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last  = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: fmt(first), end: fmt(last) };
  }
  if (key === 'last_30d') {
    const start = new Date(today); start.setDate(start.getDate() - 29);
    return { start: fmt(start), end: fmt(today) };
  }
  if (key === 'last_90d') {
    const start = new Date(today); start.setDate(start.getDate() - 89);
    return { start: fmt(start), end: fmt(today) };
  }
  if (key === 'all_time') return { start: '', end: '' };
  return { start: '', end: '' };
};

const PaymentsPage = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [leads, setLeads] = useState({});
  const [invoices, setInvoices] = useState({});
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [editInvoiceModal, setEditInvoiceModal] = useState(null);
  const [refundingPayment, setRefundingPayment] = useState(null);
  // V36.4 — Date filter state (default: all time → fast initial load)
  const [datePreset, setDatePreset] = useState('all_time');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const fetchPayments = useCallback(async (startDate = '', endDate = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.set('start_date', startDate);
        params.set('end_date', endDate);
      }
      const qs = params.toString();
      const url = `${API_URL}/api/payments${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url, { withCredentials: true });
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments(dateRange.start, dateRange.end);
    fetchLeads();
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyDatePreset = (key) => {
    setDatePreset(key);
    if (key === 'custom') return;  // wait for user to pick custom range + click Apply
    const range = buildDatePreset(key);
    setDateRange(range);
    fetchPayments(range.start, range.end);
  };

  const applyCustomDateRange = () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error('Please pick both start and end dates');
      return;
    }
    if (dateRange.start > dateRange.end) {
      toast.error('Start date must be before end date');
      return;
    }
    fetchPayments(dateRange.start, dateRange.end);
  };

  // Close status dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLeads = async () => {
    try {
      // Finance has no lead access — payer identity comes with each payment row (lead_name / lead_email).
      if (user?.role === 'finance_admin') return;
      const response = await axios.get(`${API_URL}/api/leads/all`, { withCredentials: true });
      const leadsMap = {};
      response.data.forEach(lead => {
        leadsMap[lead.lead_id] = lead;
      });
      setLeads(leadsMap);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices`, { withCredentials: true });
      const invoicesMap = {};
      response.data.forEach(invoice => {
        invoicesMap[invoice.payment_id] = invoice;
      });
      setInvoices(invoicesMap);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  // Toggle status filter for multi-select dropdown
  const toggleStatusFilter = (value) => {
    setStatusFilter(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const filteredPayments = payments.filter(payment => {
    const lead = leads[payment.lead_id] || { full_name: payment.lead_name, email: payment.lead_email };
    const matchesSearch = lead?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.payment_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.razorpay_payment_id?.toLowerCase().includes(searchTerm.toLowerCase());
    // Multi-select: if no filters selected, show all; otherwise check if status is in selected array
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(payment.payment_status);
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      created: 'badge-info',
      sent: 'badge-info',
      captured: 'badge-success',
      paid: 'badge-success',
      expired: 'badge-error',
      cancelled: 'badge-error',
      refunded: 'badge-warning'
    };
    return styles[status] || 'badge-pending';
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };


  const handleDownloadInvoice = async (paymentId) => {
    setDownloadingInvoice(paymentId);
    try {
      const response = await axios.post(
        `${API_URL}/api/invoices/generate-for-payment/${paymentId}/download`,
        {},
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `Invoice_${paymentId}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded');
      fetchInvoices(); // Refresh invoices list
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleEditInvoice = (payment) => {
    const invoice = invoices[payment.payment_id];
    const lead = leads[payment.lead_id] || { full_name: payment.lead_name, email: payment.lead_email, phone: payment.lead_phone };
    
    setEditInvoiceModal({
      payment_id: payment.payment_id,
      invoice_id: invoice?.invoice_id,
      invoice_number: invoice?.invoice_number,
      customer_name: invoice?.customer_name || lead?.full_name || '',
      customer_email: invoice?.customer_email || lead?.email || '',
      customer_phone: invoice?.customer_phone || lead?.phone || '',
      customer_address: invoice?.customer_address || '',
      service_description: invoice?.service_description || 'Soul Sanskar Program',
      tenure_months: invoice?.tenure_months ?? payment?.tenure_months ?? 0,
      is_till_delivery: invoice?.is_till_delivery ?? payment?.is_till_delivery ?? false,
      edit_reason: ''
    });
  };

  const handleRefund = async (payment) => {
    const reason = window.prompt('Enter refund reason:');
    if (!reason) {
      toast.error('Refund reason is required');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to refund ₹${payment.total_amount} to ${leads[payment.lead_id]?.full_name || payment.lead_name}?\n\nThis will also reverse any doctor commission associated with this payment.`)) {
      return;
    }
    
    setRefundingPayment(payment.payment_id);
    try {
      await axios.post(
        `${API_URL}/api/payments/${payment.payment_id}/refund`,
        { reason },
        { withCredentials: true }
      );
      toast.success('Refund processed successfully');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefundingPayment(null);
    }
  };

  const canRefund = user?.role === 'super_admin' || user?.role === 'finance_admin';
  const [tab, setTab] = useState('links');
  const [pendingRefunds, setPendingRefunds] = useState(0);
  useEffect(() => {
    if (!canRefund) return;
    axios.get(`${API_URL}/api/refund-requests?status=pending`, { withCredentials: true })
      .then((r) => setPendingRefunds((r.data?.requests || []).length)).catch(() => {});
  }, [canRefund]);
  const isTeamMember = user?.role === 'sales' || user?.role === 'pre_sales';

  return (
    <div data-testid="payments-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Payments</h1>
        <p className="text-slate-500">
          {isTeamMember 
            ? 'Payment links you have created' 
            : 'Track payment links and transactions'}
        </p>
      </div>

      {/* Info banner for team members */}
      {isTeamMember && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-indigo-600 flex-shrink-0" />
          <p className="text-sm text-indigo-700">
            You are viewing payment links that you have created. To create a new payment link, go to the Leads page and click on a lead.
          </p>
        </div>
      )}

      {/* V19 — Payment links vs learner refund requests (LMS) */}
      {canRefund && (
        <div className="flex gap-1 border-b border-slate-200" data-testid="payments-tabs">
          {[['links', 'Payment Links'], ['refunds', `Refund Requests${pendingRefunds ? ` (${pendingRefunds})` : ''}`]].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
              data-testid={`payments-tab-${key}`}>{label}</button>
          ))}
        </div>
      )}

      {tab === 'refunds' && canRefund ? (
        <RefundRequestsTab onPendingCount={setPendingRefunds} />
      ) : (<>
      {/* V36.4 — Date range filter (with quick presets + custom).
          When a range is applied, the backend lifts its 500-row cap to 5,000
          for that window, so historical synced payments become visible. */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <CalendarIcon className="w-4 h-4 text-slate-500" />
            <span className="font-medium">Date range:</span>
            <span className="text-slate-500">
              {datePreset === 'all_time'
                ? 'All time (500 most recent)'
                : dateRange.start && dateRange.end
                  ? `${dateRange.start}  →  ${dateRange.end}`
                  : 'Pick a range'}
            </span>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[
              { key: 'all_time',   label: 'All time' },
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'last_30d',   label: 'Last 30d' },
              { key: 'last_90d',   label: 'Last 90d' },
              { key: 'custom',     label: 'Custom' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyDatePreset(p.key)}
                className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                  datePreset === p.key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`pay-date-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {datePreset === 'custom' && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="input-base text-sm"
              data-testid="pay-custom-start"
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="input-base text-sm"
              data-testid="pay-custom-end"
            />
            <button
              onClick={applyCustomDateRange}
              className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              data-testid="pay-custom-apply"
            >
              Apply
            </button>
          </div>
        )}
        {(datePreset !== 'all_time' && (dateRange.start || dateRange.end)) && (
          <div className="text-[11px] text-slate-500 italic">
            Showing payments where capture date (or created date when capture is missing) falls in this range. {payments.length === 5000 && <span className="text-amber-600 font-semibold">⚠ Hit 5,000-row cap — narrow the range.</span>}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by lead name or payment ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-base pl-10"
            data-testid="search-payments"
          />
        </div>
        <div className="relative" ref={statusDropdownRef}>
          <button
            type="button"
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            className="input-base w-full sm:w-56 flex items-center justify-between"
            data-testid="filter-payment-status"
          >
            <span className="truncate">
              {statusFilter.length === 0
                ? 'All Status'
                : statusFilter.length === 1
                  ? STATUS_OPTIONS.find(o => o.value === statusFilter[0])?.label
                  : `${statusFilter.length} selected`}
            </span>
            <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
          </button>
          {statusDropdownOpen && (
            <div className="absolute z-20 mt-1 w-full sm:w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
              <button
                type="button"
                onClick={() => setStatusFilter([])}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 ${statusFilter.length === 0 ? 'text-indigo-600 font-medium' : 'text-slate-700'}`}
                data-testid="status-option-all"
              >
                {statusFilter.length === 0 && <Check className="w-4 h-4" />}
                {statusFilter.length > 0 && <span className="w-4" />}
                All Status
              </button>
              {STATUS_OPTIONS.map(opt => {
                const isSelected = statusFilter.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleStatusFilter(opt.value)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 ${isSelected ? 'text-indigo-600 font-medium' : 'text-slate-700'}`}
                    data-testid={`status-option-${opt.value}`}
                  >
                    <span className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper overflow-x-auto">
        <table className="w-full">
          <thead className="table-header">
            <tr>
              <th className="p-4 text-left">Lead</th>
              <th className="p-4 text-left">Amount</th>
              <th className="p-4 text-left">Tenure</th>
              <th className="p-4 text-left">Due Date</th>
              <th className="p-4 text-left">Status</th>
              <th className="p-4 text-left">Link</th>
              <th className="p-4 text-left">Invoice</th>
              <th className="p-4 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-slate-500">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                </td>
              </tr>
            ) : filteredPayments.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-slate-500">
                  No payments found
                </td>
              </tr>
            ) : (
              filteredPayments.map((payment) => {
                const lead = leads[payment.lead_id] || { full_name: payment.lead_name, email: payment.lead_email };
                const invoice = invoices[payment.payment_id];
                const isPaid = payment.payment_status === 'paid' || payment.payment_status === 'captured';
                
                return (
                  <tr key={payment.payment_id} className="table-row" data-testid={`payment-row-${payment.payment_id}`}>
                    <td className="table-cell">
                      <div className="font-medium text-slate-900">{lead?.full_name || 'Unknown'}</div>
                      <div className="text-xs text-slate-500">{lead?.email}</div>
                    </td>
                    <td className="table-cell">
                      <div className="font-semibold text-slate-900">
                        {formatCurrency(payment.total_amount, payment.currency)}
                      </div>
                      <div className="text-xs text-slate-500">
                        Base: {formatCurrency(payment.base_amount || payment.total_amount, payment.currency)}
                        {payment.discount_amount > 0 && (
                          <span className="text-green-600"> (-{formatCurrency(payment.discount_amount, payment.currency)})</span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span>{payment.tenure_months} month{payment.tenure_months > 1 ? 's' : ''}</span>
                    </td>
                    <td className="table-cell">
                      <span>{payment.due_date}</span>
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(payment.payment_status)}`}>
                        {payment.payment_status.toUpperCase()}
                      </span>
                    </td>
                    <td className="table-cell">
                      {payment.razorpay_short_url && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyToClipboard(payment.razorpay_short_url)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md"
                            title="Copy link"
                            data-testid={`copy-link-${payment.payment_id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            href={payment.razorpay_short_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-md"
                            title="Open link"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      {isPaid ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDownloadInvoice(payment.payment_id)}
                            disabled={downloadingInvoice === payment.payment_id}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                            title="Download Invoice"
                            data-testid={`download-invoice-${payment.payment_id}`}
                          >
                            {downloadingInvoice === payment.payment_id ? (
                              <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                          {invoice && (
                            <button
                              onClick={() => handleEditInvoice(payment)}
                              className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors"
                              title="Edit Invoice Details"
                              data-testid={`edit-invoice-${payment.payment_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {invoice && (
                            <span className="text-xs text-slate-400 ml-1" title={`Invoice ${invoice.invoice_number}`}>
                              {invoice.invoice_number}
                            </span>
                          )}
                          {invoice && invoice.email_status === 'sent' && (
                            <span
                              className="inline-flex items-center text-emerald-600 ml-0.5"
                              title={`Invoice emailed${invoice.email_to ? ` to ${invoice.email_to}` : ''}${invoice.email_sent_at ? ` on ${new Date(invoice.email_sent_at).toLocaleString()}` : ''}`}
                              data-testid={`invoice-email-sent-${payment.payment_id}`}
                            >
                              <MailCheck className="w-4 h-4" />
                            </span>
                          )}
                          {invoice && invoice.email_status === 'failed' && (
                            <span
                              className="inline-flex items-center text-red-600 ml-0.5 cursor-help"
                              title={`Invoice NOT emailed. Reason: ${invoice.email_error || 'Unknown error'}. Open Edit Invoice to correct the email and resend, or download the PDF.`}
                              data-testid={`invoice-email-failed-${payment.payment_id}`}
                            >
                              <MailX className="w-4 h-4" />
                            </span>
                          )}
                          {invoice && !invoice.email_status && (
                            <span
                              className="inline-flex items-center text-amber-500 ml-0.5 cursor-help"
                              title="Invoice email not attempted yet. Use Edit Invoice → Save & Resend to email it."
                              data-testid={`invoice-email-none-${payment.payment_id}`}
                            >
                              <MailWarning className="w-4 h-4" />
                            </span>
                          )}
                          {/* Refund Button */}
                          {canRefund && (payment.payment_status === 'paid' || payment.payment_status === 'captured') && (
                            <button
                              onClick={() => handleRefund(payment)}
                              disabled={refundingPayment === payment.payment_id}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors ml-2"
                              title="Process Refund"
                              data-testid={`refund-${payment.payment_id}`}
                            >
                              {refundingPayment === payment.payment_id ? (
                                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className="text-sm text-slate-500" title={payment.captured_at || payment.created_at}>
                        {new Date(payment.captured_at || payment.created_at).toLocaleDateString()}
                      </span>
                      {payment.captured_at && payment.captured_at !== payment.created_at && (
                        <div className="text-xs text-slate-400">
                          Created: {new Date(payment.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      </>)}

      {/* Edit Invoice Modal */}
      {editInvoiceModal && (
        <EditInvoiceModal
          data={editInvoiceModal}
          onClose={() => setEditInvoiceModal(null)}
          onSave={() => {
            setEditInvoiceModal(null);
            fetchInvoices();
          }}
        />
      )}
    </div>
  );
};

// Edit Invoice Modal Component
const EditInvoiceModal = ({ data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    customer_name: data.customer_name,
    customer_email: data.customer_email,
    customer_phone: data.customer_phone,
    customer_address: data.customer_address || '',
    service_description: data.service_description || 'Soul Sanskar Program',
    tenure_months: data.tenure_months ?? 0,
    is_till_delivery: data.is_till_delivery ?? false,
    edit_reason: ''
  });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!data.invoice_id) {
      toast.error('Invoice not found. Please download the invoice first.');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('customer_name', formData.customer_name);
      params.append('customer_email', formData.customer_email);
      params.append('customer_phone', formData.customer_phone);
      params.append('customer_address', formData.customer_address);
      params.append('service_description', formData.service_description);
      params.append('tenure_months', String(parseInt(formData.tenure_months || 0, 10)));
      params.append('is_till_delivery', String(!!formData.is_till_delivery));
      if (formData.edit_reason) {
        params.append('edit_reason', formData.edit_reason);
      }

      await axios.put(
        `${API_URL}/api/invoices/${data.invoice_id}?${params}`,
        {},
        { withCredentials: true }
      );

      toast.success('Invoice details updated. Download again to get updated PDF.');
      onSave();
    } catch (error) {
      toast.error('Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (!data.invoice_id) {
      toast.error('Invoice not found. Please download the invoice first.');
      return;
    }
    if (!formData.customer_email) {
      toast.error('Customer email is required to resend.');
      return;
    }
    setResending(true);
    try {
      // Save first, then resend
      const params = new URLSearchParams();
      params.append('customer_name', formData.customer_name);
      params.append('customer_email', formData.customer_email);
      params.append('customer_phone', formData.customer_phone);
      params.append('customer_address', formData.customer_address);
      params.append('service_description', formData.service_description);
      params.append('tenure_months', String(parseInt(formData.tenure_months || 0, 10)));
      params.append('is_till_delivery', String(!!formData.is_till_delivery));
      if (formData.edit_reason) params.append('edit_reason', formData.edit_reason);

      await axios.put(`${API_URL}/api/invoices/${data.invoice_id}?${params}`, {}, { withCredentials: true });
      
      const res = await axios.post(
        `${API_URL}/api/invoices/${data.invoice_id}/resend`,
        {},
        { withCredentials: true }
      );
      if (res.data.success === false) {
        toast.error(res.data.message || 'Failed to resend invoice');
      } else {
        toast.success(res.data.message || 'Invoice resent!');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invoice');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Edit Invoice Details</h3>
            <p className="text-sm text-slate-500">{data.invoice_number || 'New Invoice'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Note:</strong> Editing these details will update the invoice for reimbursement purposes. 
            The original lead details remain unchanged.
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Customer Name *</label>
            <input
              type="text"
              required
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              className="input-base"
              placeholder="Name as it should appear on invoice"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="text"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Billing Address</label>
            <textarea
              value={formData.customer_address}
              onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
              className="input-base"
              rows="3"
              placeholder="Company name, address for reimbursement..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Description</label>
            <input
              type="text"
              value={formData.service_description}
              onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
              className="input-base"
              placeholder="e.g., Soul Sanskar Program - Till Delivery"
              data-testid="edit-service-description"
            />
            <p className="text-xs text-slate-500 mt-1">Edit description as it appears on the invoice</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration (months)</label>
              <input
                type="number"
                min="0"
                step="1"
                disabled={formData.is_till_delivery}
                value={formData.tenure_months}
                onChange={(e) => setFormData({ ...formData, tenure_months: e.target.value })}
                className="input-base disabled:bg-slate-100 disabled:text-slate-400"
                placeholder="0 = one-time / class"
                data-testid="edit-invoice-tenure-months"
              />
              <p className="text-xs text-slate-500 mt-1">0 = one-time charge / single class</p>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_till_delivery}
                  onChange={(e) => setFormData({ ...formData, is_till_delivery: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="edit-invoice-till-delivery"
                />
                <span>Till Delivery</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason for Edit</label>
            <input
              type="text"
              value={formData.edit_reason}
              onChange={(e) => setFormData({ ...formData, edit_reason: e.target.value })}
              className="input-base"
              placeholder="e.g., Customer requested name change for reimbursement"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-secondary" data-testid="save-invoice-btn">
              {saving ? 'Saving...' : 'Save Only'}
            </button>
            <button type="button" onClick={handleResend} disabled={resending || saving} className="btn-primary flex items-center gap-2" data-testid="resend-invoice-btn">
              {resending ? 'Sending...' : 'Save & Resend Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentsPage;
